import { observable } from "@legendapp/state"
import type { TPhoneStatus } from "@/schemas/user.schema"

export interface TRosterUser {
  id: string
  __key: string
  fullName: string
  email?: string | null
  phoneStatus: TPhoneStatus
  ring?: Record<string, unknown> | null
  instaCall?: Record<string, unknown> | null
  [key: string]: unknown
}

interface TRosterStore {
  users: Record<string, TRosterUser>
}

export const rosterStore$ = observable<TRosterStore>({
  users: {},
})

function normalizePhoneStatus(status: unknown): TPhoneStatus {
  const normalized = `${status ?? "AVAILABLE"}`.toUpperCase()
  if (normalized === "TALKING" || normalized === "RINGING" || normalized === "NO_ANSWER_COOLDOWN" || normalized === "AVAILABLE") {
    return normalized
  }

  return "AVAILABLE"
}

function buildFullName(rawUser: Record<string, unknown>, fallbackID: string) {
  const fullName = `${rawUser.fullName ?? ""}`.trim()
  if (fullName) return fullName

  const name = `${rawUser.name ?? ""}`.trim()
  const lastName = `${rawUser.lastName ?? ""}`.trim()
  const combinedName = `${name} ${lastName}`.trim()
  if (combinedName) return combinedName

  return fallbackID
}

export function upsertRosterUser(key: string, rawData: unknown) {
  const safeRaw = (rawData && typeof rawData === "object" ? rawData : {}) as Record<string, unknown>
  const normalizedID = safeRaw.id != null ? `${safeRaw.id}` : `${key}`

  const normalizedUser: TRosterUser = {
    ...safeRaw,
    id: normalizedID,
    __key: key,
    fullName: buildFullName(safeRaw, normalizedID),
    phoneStatus: normalizePhoneStatus(safeRaw.phoneStatus),
    ring: (safeRaw.ring as Record<string, unknown> | null) ?? null,
    instaCall: (safeRaw.instaCall as Record<string, unknown> | null) ?? null,
  }

  rosterStore$.users[key].set(normalizedUser)
}

export function removeRosterUser(key: string) {
  rosterStore$.users[key].delete()
}

export function replaceRosterUsers(nextUsers: Record<string, unknown>) {
  const normalized: Record<string, TRosterUser> = {}

  for (const [key, value] of Object.entries(nextUsers)) {
    const safeRaw = (value && typeof value === "object" ? value : {}) as Record<string, unknown>
    const normalizedID = safeRaw.id != null ? `${safeRaw.id}` : `${key}`

    normalized[key] = {
      ...safeRaw,
      id: normalizedID,
      __key: key,
      fullName: buildFullName(safeRaw, normalizedID),
      phoneStatus: normalizePhoneStatus(safeRaw.phoneStatus),
      ring: (safeRaw.ring as Record<string, unknown> | null) ?? null,
      instaCall: (safeRaw.instaCall as Record<string, unknown> | null) ?? null,
    }
  }

  rosterStore$.users.set(normalized)
}

export function clearRosterStore() {
  rosterStore$.users.set({})
}
